import {
  type AsyncCollectionT,
  type AsyncDataEnvelopeT,
  newAsyncDataEnvelope,
  withGlobalStateType,
} from '@dr.pogodin/react-utils';

import type {
  GetAggregatedUserScoresResT,
  GetHistoricalUserScoresResT,
} from 'server/controllers/user-scores';

import type { ArtistListT, ArtistT } from './artist';
import type { AssetT, ImageAssetT } from './assets';
import type { BlogItemT } from './blog';
import type { ExperienceT } from './experience';
import type { QuestionnaireT } from './questionnaire';
import type { ResearchLibCategoryT, ResearchLibLinkT } from './research-lib';
import type { PastSessionsT, SessionKindT, SessionT } from './sessions';

import type {
  NumericUserScoreKindT,
  RankingUserScoreKindT,
  UserScoreT,
} from './user-scores';

import type { UserListT, UserT } from './users';
import type { VideoT } from './video';
import type { PublicVimeoVideoDataT } from './vimeo';

/** Inits a clean ExperienceDataT object. */
export function initExperienceData(): ExperienceT {
  return {
    artists: [],
    bodyAreas: [],
    bodyPositions: [],
    createdBy: undefined,
    difficulty: undefined,
    durationMin: 0,
    emotionalMechanisms: [],
    environmentNeeds: undefined,
    equipmentNeeds: [],
    id: undefined,
    intent: undefined,
    isAvailable: true,
    kind: 'user',
    lifeMomentId: '99_UNIQUE_UNKNOWN_LIFE_MOMENT',
    modalitiesV1: [],
    modalitiesV2: [],
    neurologicalMechanisms: [],
    physicalMechanisms: [],
    shiftFrom: [],
    shiftTo: [],
    title: undefined,
    transformationStages: [],
    videos: [],
  };
}

export type QuestionnaireStateT = {
  data: QuestionnaireT;
  nextQuestionIdx: number;
};

export type BlogItemsListT = {
  hasMore: boolean;
  items: BlogItemT[];
  lastPage: number;
  numPages: number | undefined;
  pageSize: number;
  queryString: string;
};

// TODO: Later react-global-state will provide necessary global state segment
// type, and useAsyncList() hook, modelled exactly after the pattern used in
// this app, with appropriate further generalizations.
export type VideoListT = {
  hasMore: boolean;
  items: VideoT[];
  lastPage: number;
  numPages: number | undefined;
  pageSize: number;

  // The stringified query for which the items stored in the state have been
  // retrieved, i.e. if this stored value does not match a new stringified
  // query we need to reload all items.
  queryString: string;
};

/**
 * Our app kind of packs together these separate apps, served from different
 * subdomains. The way it works - when HTTP request hits the server, it selects
 * one of these values, based on the URL, and saves it into the global state,
 * which is used during the server-side rendering, and then injected to
 * the client-side.
 */
export type WebAppT = 'Admin' | 'Primary' | 'Website';

type AddNewAdminModalT = 'CREATED_NEW' | 'ERROR' | 'PROMOTED_EXISTING';

export type AddNewPractitionerModalT = 'ALREADY_PRACTITIONER' | 'CREATED_NEW'
  | 'ERROR' | 'PROMOTED_EXISTING';

type PractitionerApplicationResT = 'ALREADY_APPLIED_BEFORE' | 'APPLICATION_SENT';

export type ExperienceBuildMetaT = {
  steps: string[];
  title: string;
  transcript: string;
};

export type ExperienceAndSessionMetaT = {
  checkInId: null | string;
  experience: ExperienceT;

  /**
   * The kind of session, if any, to set when the session for this experience is
   * initiated (it is recommended by the prescription engine alongside with the
   * experience, based on the check-in information).
   */
  sessionKind: SessionKindT | null;
};

export type GlobalStateT = {
  // NOTE: For now it does not support the pagination (like, say, the "artists"
  // segment does), because we don't expect to have too many admins at any point,
  // thus supporting the pagination for them is probably an overkill, at least
  // for now.
  adminUsers: AsyncDataEnvelopeT<UserT[]>;

  artists: {
    list: AsyncDataEnvelopeT<ArtistListT>;
    map: Record<string, AsyncDataEnvelopeT<ArtistT>>;

    /** Separate list for the public (unauthenticated) practitioners page. */
    publicList: AsyncDataEnvelopeT<ArtistListT>;
  };

  artistGalleries: Record<string, AsyncDataEnvelopeT<ImageAssetT[]>>;
  artistProfileHeroImages: Record<string, AsyncDataEnvelopeT<ImageAssetT>>;

  assets: {
    byUser: Record<string, AsyncDataEnvelopeT<AssetT[]>>;
  };

  blog: {
    list: AsyncDataEnvelopeT<BlogItemsListT>;
    map: Record<string, AsyncDataEnvelopeT<BlogItemT>>;
    youMayAlsoLike: Record<string, AsyncDataEnvelopeT<BlogItemT[]>>;
  };

  /** Holds data of the video being edited in CMS pages related
   *  to video editing. */
  editedVideo: AsyncDataEnvelopeT<VideoT>;

  experience: AsyncDataEnvelopeT<ExperienceAndSessionMetaT>;
  experienceBuildMeta: AsyncDataEnvelopeT<ExperienceBuildMetaT>;
  experiences: Record<string, AsyncDataEnvelopeT<ExperienceT>>;
  pastSessions: AsyncDataEnvelopeT<PastSessionsT>;
  questionnaire: AsyncDataEnvelopeT<QuestionnaireStateT>;

  researchLib: {
    categories: AsyncDataEnvelopeT<ResearchLibCategoryT[]>;
    links: AsyncDataEnvelopeT<ResearchLibLinkT[]>;
  };

  session: AsyncDataEnvelopeT<SessionT>;

  // The latest "Today's wellness check-in" questionnaire, started by user
  // (it might be incomplete), if one has been started on the current date
  // in the user's timezone.
  todayWellnessCheck: AsyncDataEnvelopeT<QuestionnaireT | null>;

  ui: {
    addNewAdmin?: {
      modal?: AddNewAdminModalT;
    };

    addNewPractitioner?: {
      modal?: AddNewPractitionerModalT;
    };

    // NOTE: "undefined" means the sidebar has not been explicitly collapsed
    // by the admin user yet in the current app run, thus it defaults to
    // expanded.
    adminSidebarExpanded?: boolean;

    // Set when realtime voice output failed to autoplay (iOS gesture-less
    // block); drives the "Tap to enable sound" overlay.
    audioOutputBlocked?: boolean;

    // NOTE: "undefined" means the Audio Setup page has not been displayed
    // to the user yet (in the current app run), thus should be opened
    // automatically if/once appropriate.
    audioSetup?: boolean;

    blockedAudioDeviceAccess?: boolean;
    leftSidebarExpanded?: boolean;

    // Mic capture stopped and re-acquiring it needs a user gesture; drives the
    // "tap to resume" prompt.
    microphoneLost?: boolean;

    mobileMenuOpen?: boolean;

    practitionerApplicationRes?: PractitionerApplicationResT;

    // The realtime connection dropped; drives the "session has ended" notice.
    sessionDisconnected?: boolean;

    videoBeingDragged?: VideoT;
  };

  user: UserT | null;

  /** Artist record of the user, if the user is a practitioner. */
  userArtist: AsyncDataEnvelopeT<ArtistT>;

  userScores: AsyncDataEnvelopeT<
    Partial<Record<NumericUserScoreKindT, UserScoreT>>
  >;
  userScoresAggregated: AsyncDataEnvelopeT<GetAggregatedUserScoresResT>;
  userScoresHistorical: AsyncDataEnvelopeT<GetHistoricalUserScoresResT>;
  userScoresRanking: AsyncDataEnvelopeT<
    Partial<Record<RankingUserScoreKindT, UserScoreT>>
  >;

  /**
   * This segment is intended for user management in the admin panel.
   */
  users: {
    list: AsyncDataEnvelopeT<UserListT>;
    map: AsyncCollectionT<UserT, string>;
  };

  videos: AsyncDataEnvelopeT<VideoListT>;

  /**
   * TODO: We need to move GetVimeoVideoDataT type to some shared location,
   * to be able to re-use it here, without creating circular imports.
   */
  vimeoVideoData: Record<string, AsyncDataEnvelopeT<PublicVimeoVideoDataT>>;

  webApp: WebAppT;
};

export function initGlobalState(
  user: UserT | undefined,
  webApp: WebAppT,
): GlobalStateT {
  return {
    adminUsers: newAsyncDataEnvelope(),
    artistGalleries: {},
    artistProfileHeroImages: {},
    artists: {
      list: newAsyncDataEnvelope(),
      map: {},
      publicList: newAsyncDataEnvelope(),
    },
    assets: {
      byUser: {},
    },
    blog: {
      list: newAsyncDataEnvelope(),
      map: {},
      youMayAlsoLike: {},
    },
    editedVideo: newAsyncDataEnvelope(),
    experience: newAsyncDataEnvelope({
      checkInId: null,
      experience: initExperienceData(),
      sessionKind: null,
    }, {
      timestamp: Date.now(),
    }),
    experienceBuildMeta: newAsyncDataEnvelope(),
    experiences: {},
    pastSessions: newAsyncDataEnvelope(),
    questionnaire: newAsyncDataEnvelope(),
    researchLib: {
      categories: newAsyncDataEnvelope(),
      links: newAsyncDataEnvelope(),
    },
    session: newAsyncDataEnvelope(),
    todayWellnessCheck: newAsyncDataEnvelope(),
    ui: {},
    user: user ?? null,
    userArtist: newAsyncDataEnvelope(),
    userScores: newAsyncDataEnvelope(),
    userScoresAggregated: newAsyncDataEnvelope(),
    userScoresHistorical: newAsyncDataEnvelope(),
    userScoresRanking: newAsyncDataEnvelope(),
    users: {
      list: newAsyncDataEnvelope(),
      map: {},
    },
    videos: newAsyncDataEnvelope(),
    vimeoVideoData: {},
    webApp,
  };
}

const {
  useAsyncCollection,
  useAsyncData,
  useGlobalState,
  useGlobalStateObject,
} = withGlobalStateType<GlobalStateT>();

export {
  newAsyncDataEnvelope,
  useAsyncCollection,
  useAsyncData,
  useGlobalState,
  useGlobalStateObject,
};
